import React, { useState, useEffect } from 'react';
import Sidebar from '../../components/Sidebar';
import Header from '../../components/Header';
import { Plus, Edit, Trash2, ChevronLeft, ChevronRight, X, ArrowLeft, Eye, Check, Calendar, User, Home } from 'lucide-react';
import apiService from '../../utils/api';
import { useToast } from '../../components/Toast';
import { sortedByName } from '../../utils/sortAlpha';
const TODAY_DATE = new Date().toISOString().split('T')[0];
// CSS handled by global theme.css

function KhataPage() {
  const toast = useToast();
  const [data, setData] = useState([]);
  const [tabelas, setTabelas] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // View states
  const [currentView, setCurrentView] = useState('list'); // 'list', 'form', 'detail'
  const [editingItem, setEditingItem] = useState(null);
  const [viewingItem, setViewingItem] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    tabela_id: '',
    employee_id: '',
    khata_no: '',
    total_kila: '',
    kila_occupied: ''
  });

  // List pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Filter states
  const [filters, setFilters] = useState({
    tabela_name: '',
    employee_name: '',
    khata_no: '',
    total_kila: '',
    kila_occupied: ''
  });

  // Detail view data
  const [milkData, setMilkData] = useState([]);
  const [summary, setSummary] = useState({});
  const [milkPagination, setMilkPagination] = useState({});
  const [milkCurrentPage, setMilkCurrentPage] = useState(1);
  const [dateFilters, setDateFilters] = useState({
    start_date: '',
    end_date: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [khatasResponse, tabelasResponse, employeesResponse] = await Promise.all([
        apiService.getKhata(),
        apiService.getTabela(),
        apiService.getEmployees()
      ]);
      
      setData(khatasResponse.data || []);
      setTabelas(tabelasResponse.data || []);
      setEmployees(employeesResponse.data || []);
      setError(null);
    } catch (error) {
      setError(error.message);
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadKhataMilkCollection = async (khataId, page = 1, filters = {}) => {
    try {
      setLoading(true);
      const params = {
        page,
        per_page: 50,
        ...filters
      };
      
      const response = await apiService.getKhataMilkCollection(khataId, params);
      setMilkData(response.data || []);
      setSummary(response.summary || {});
      setMilkPagination(response.pagination || {});
      setError(null);
    } catch (error) {
      setError(error.message);
      console.error('Error loading milk collection:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTabelaName = (tabelaId) => {
    const tabela = tabelas.find(t => parseInt(t.tabela_id) === parseInt(tabelaId));
    return tabela ? tabela.name : 'Unknown';
  };
  
  const getEmployeeName = (employeeId) => {
    const employee = employees.find(e => parseInt(e.employee_id) === parseInt(employeeId));
    return employee ? employee.name : 'Unknown';
  };

  // Filter data
  const filteredData = data.filter(item => {
    const tabelaName = item.tabela_name || getTabelaName(item.tabela_id);
    const employeeName = item.employee_name || getEmployeeName(item.employee_id);
    
    return (
      (filters.tabela_name === '' || tabelaName.toLowerCase().includes(filters.tabela_name.toLowerCase())) &&
      (filters.employee_name === '' || employeeName.toLowerCase().includes(filters.employee_name.toLowerCase())) &&
      (filters.khata_no === '' || item.khata_no.toString().includes(filters.khata_no)) &&
      (filters.total_kila === '' || item.total_kila.toString().includes(filters.total_kila)) &&
      (filters.kila_occupied === '' || item.kila_occupied.toString().includes(filters.kila_occupied))
    );
  });

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = filteredData.slice(startIndex, startIndex + itemsPerPage);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  const handleAdd = () => {
    setEditingItem(null);
    setFormData({
      tabela_id: '',
      employee_id: '',
      khata_no: '',
      total_kila: '',
      kila_occupied: ''
    });
    setCurrentView('form');
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      tabela_id: item.tabela_id,
      employee_id: item.employee_id,
      khata_no: item.khata_no,
      total_kila: item.total_kila,
      kila_occupied: item.kila_occupied
    });
    setCurrentView('form');
  };

  const handleView = (item) => {
    setViewingItem(item);
    setCurrentView('detail');
    setMilkCurrentPage(1);
    setDateFilters({ start_date: '', end_date: '' });
    loadKhataMilkCollection(item.khata_id, 1);
  };

  const handleDelete = async (item) => {
    if (window.confirm('Are you sure you want to delete this khata?')) {
      try {
        await apiService.deleteKhata(item.tabela_id, item.khata_no);
        await loadData();
        
        const newTotalPages = Math.ceil((filteredData.length - 1) / itemsPerPage);
        if (currentPage > newTotalPages && newTotalPages > 0) {
          setCurrentPage(newTotalPages);
        }
      } catch (error) {
        toast.error('Error deleting khata: ' + error.message);
      }
    }
  };

  const handleSubmit = async () => {
    if (!formData.tabela_id || !formData.employee_id || !formData.khata_no || 
        !formData.total_kila || !formData.kila_occupied) {
      toast.warning('All fields are required.');
      return;
    }

    const submitData = {
      ...formData,
      tabela_id: parseInt(formData.tabela_id),
      employee_id: parseInt(formData.employee_id),
      khata_no: parseInt(formData.khata_no),
      total_kila: parseInt(formData.total_kila),
      kila_occupied: parseInt(formData.kila_occupied)
    };

    try {
      setLoading(true);
      if (editingItem) {
        await apiService.updateKhata(editingItem.tabela_id, editingItem.khata_no, submitData);
      } else {
        await apiService.createKhata(submitData);
      }
      
      await loadData();
      setCurrentView('list');
      setFormData({
        tabela_id: '',
        employee_id: '',
        khata_no: '',
        total_kila: '',
        kila_occupied: ''
      });
      setEditingItem(null);
    } catch (error) {
      toast.error('Error saving khata: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    });
  };

  const clearFilters = () => {
    setFilters({
      tabela_name: '',
      employee_name: '',
      khata_no: '',
      total_kila: '',
      kila_occupied: ''
    });
  };

  const handleCancel = () => {
    setCurrentView('list');
    setFormData({
      tabela_id: '',
      employee_id: '',
      khata_no: '',
      total_kila: '',
      kila_occupied: ''
    });
    setEditingItem(null);
    setViewingItem(null);
    setError(null);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleMilkPageChange = (page) => {
    setMilkCurrentPage(page);
    loadKhataMilkCollection(viewingItem.khata_id, page, dateFilters);
  };

  const handleDateFilterChange = (e) => {
    setDateFilters({
      ...dateFilters,
      [e.target.name]: e.target.value
    });
  };

  const applyDateFilters = () => {
    setMilkCurrentPage(1);
    loadKhataMilkCollection(viewingItem.khata_id, 1, dateFilters);
  };

  const clearDateFilters = () => {
    setDateFilters({ start_date: '', end_date: '' });
    setMilkCurrentPage(1);
    loadKhataMilkCollection(viewingItem.khata_id, 1);
  };

  const renderLayout = (content) => (
    <div className="app-container">
      <Header />
      <div className="main-content">
        <Sidebar />
        <div className="page-content">
          {content}
        </div>
      </div>
    </div>
  );

  if (loading && data.length === 0) {
    return renderLayout(
        <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading khatas...</p>
        </div>
    );
  }

  if (error && data.length === 0) {
    return renderLayout(
        <div className="error-container">
            <h3>Error loading khatas</h3>
            <p>{error}</p>
            <button className="btn btn-primary" onClick={loadData}>Retry</button>
        </div>
    );
  }

  // Detail View
  if (currentView === 'detail') {
    const khata = viewingItem;
    
    return renderLayout(
      <>
        <div className="page-header">
          <div className="header-left">
            <button className="btn btn-secondary" onClick={handleCancel} style={{ marginRight: '1rem' }}>
              <ArrowLeft size={18} /> Back
            </button>
            <h1 className="page-title">
              Khata Details - #{khata.khata_no}
            </h1>
          </div>
          <button className="btn btn-primary" onClick={() => handleEdit(khata)}>
            <Edit size={18} /> Edit Khata
          </button>
        </div>
        
        {error && (
          <div className="alert alert-error">
            <p>{error}</p>
          </div>
        )}
        
        <div className="form-row">
            <div className="card" style={{ flex: 1 }}>
                <div className="card-header">
                    <h3 className="card-title">Khata Information</h3>
                </div>
                <div className="form-group">
                    <label className="form-label">Tabela</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1.05rem' }}>
                        <Home size={18} />
                        {khata.tabela_name || getTabelaName(khata.tabela_id)}
                    </div>
                </div>
                <div className="form-group">
                    <label className="form-label">Manager</label>
                     <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1.05rem' }}>
                        <User size={18} />
                        {khata.employee_name || getEmployeeName(khata.employee_id)}
                    </div>
                </div>
                <div className="form-group">
                     <label className="form-label">Space Utilization</label>
                     <div style={{ background: '#f8fafc', padding: '1rem', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                            <span>Occupied</span>
                            <strong>{khata.kila_occupied}</strong>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                            <span>Total Capacity</span>
                            <strong>{khata.total_kila}</strong>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #e2e8f0', paddingTop: '0.5rem', color: 'var(--primary-color)' }}>
                            <span>Available</span>
                            <strong>{khata.total_kila - khata.kila_occupied}</strong>
                        </div>
                     </div>
                </div>
            </div>

            <div className="card" style={{ flex: 1 }}>
                <div className="card-header">
                    <h3 className="card-title">Performance Summary</h3>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div className="stat-box" style={{ background: '#eff6ff', padding: '1.5rem', borderRadius: '8px', textAlign: 'center', gridColumn: '1 / -1' }}>
                        <div style={{ fontSize: '0.9rem', color: '#1d4ed8', marginBottom: '0.5rem' }}>Total Milk Collection</div>
                        <div style={{ fontSize: '2rem', fontWeight: '700', color: '#1e40af' }}>
                            {summary.total_collection?.toFixed(2) || 0} L
                        </div>
                    </div>
                    <div className="stat-box" style={{ background: '#f0fdf4', padding: '1rem', borderRadius: '8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '0.9rem', color: '#15803d' }}>Active Animals</div>
                        <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#166534' }}>
                            {summary.active_animals || 0}
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div className="card" style={{ marginTop: '2rem' }}>
          <div className="card-header">
            <h3 className="card-title">Daily Milk Collection</h3>
            <div className="filter-bar" style={{ marginBottom: 0 }}>
              <div className="filter-group">
                <input
                  type="date" max={TODAY_DATE}
                  name="start_date"
                  className="form-input"
                  value={dateFilters.start_date}
                  onChange={handleDateFilterChange}
                />
              </div>
              <div className="filter-group">
                <input
                  type="date" max={TODAY_DATE}
                  name="end_date"
                  className="form-input"
                  value={dateFilters.end_date}
                  onChange={handleDateFilterChange}
                />
              </div>
              <button className="btn btn-primary" onClick={applyDateFilters}>
                <Calendar size={16} /> Apply
              </button>
              <button className="btn btn-secondary" onClick={clearDateFilters}>
                Clear
              </button>
            </div>
          </div>
          
          <div className="table-container" style={{ margin: 0, boxShadow: 'none', border: 'none' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Cow Mor</th>
                  <th>Cow Eve</th>
                  <th>Cow Tot</th>
                  <th>Buff Mor</th>
                  <th>Buff Eve</th>
                  <th>Buff Tot</th>
                  <th>Total</th>
                  <th>Animals</th>
                </tr>
              </thead>
              <tbody>
                {milkData.map((item, index) => (
                  <tr key={index}>
                    <td data-label="Date">{new Date(item.date).toLocaleDateString()}</td>
                    <td data-label="Cow Mor">{item.cow_morning.toFixed(2)}</td>
                    <td data-label="Cow Eve">{item.cow_night.toFixed(2)}</td>
                    <td data-label="Cow Tot"><strong>{item.cow_total.toFixed(2)}</strong></td>
                    <td data-label="Buff Mor">{item.buffalo_morning.toFixed(2)}</td>
                    <td data-label="Buff Eve">{item.buffalo_night.toFixed(2)}</td>
                    <td data-label="Buff Tot"><strong>{item.buffalo_total.toFixed(2)}</strong></td>
                    <td data-label="Total"><strong style={{color: 'var(--primary-color)'}}>{item.total_milk.toFixed(2)}</strong></td>
                    <td data-label="Animals">
                      <span title={`Cows: ${item.cow_count}, Buffalos: ${item.buffalo_count}`}>
                        {item.total_animals}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {milkData.length === 0 && !loading && (
              <div className="no-data">
                <p>No milk collection data found for the selected date range</p>
              </div>
            )}
          </div>
          
          {milkPagination.pages > 1 && (
            <div className="pagination">
              <button 
                className="pagination-btn"
                onClick={() => handleMilkPageChange(milkCurrentPage - 1)}
                disabled={milkCurrentPage === 1}
              >
                <ChevronLeft size={16} />
              </button>
              
              {[...Array(milkPagination.pages)].map((_, index) => (
                <button
                  key={index + 1}
                  className={`pagination-btn ${milkCurrentPage === index + 1 ? 'active' : ''}`}
                  onClick={() => handleMilkPageChange(index + 1)}
                >
                  {index + 1}
                </button>
              ))}
              
              <button 
                className="pagination-btn"
                onClick={() => handleMilkPageChange(milkCurrentPage + 1)}
                disabled={milkCurrentPage === milkPagination.pages}
              >
                <ChevronRight size={16} />
              </button>
            </div>
          )}
        </div>
      </>
    );
  }

  // Form View
  if (currentView === 'form') {
    return renderLayout(
      <>
        <div className="page-header">
           <div className="header-left">
            <button className="btn btn-secondary" onClick={handleCancel} style={{ marginRight: '1rem' }}>
              <ArrowLeft size={18} /> Cancel
            </button>
            <h1 className="page-title">
               {editingItem ? 'Edit Khata' : 'Add New Khata'}
            </h1>
           </div>
        </div>
        
        {error && (
            <div className="alert alert-error">
            <p>{error}</p>
            </div>
        )}
        
        <div className="card" style={{ maxWidth: '800px', margin: '0 auto' }}>
            <div className="form-group">
                <label htmlFor="tabela_id" className="form-label">Tabela *</label>
                <select
                    id="tabela_id"
                    name="tabela_id"
                    className="form-select"
                    value={formData.tabela_id}
                    onChange={handleInputChange}
                    disabled={loading}
                >
                    <option value="">Select Tabela</option>
                    {sortedByName(tabelas).map(tabela => (
                        <option key={tabela.tabela_id} value={tabela.tabela_id}>
                        {tabela.name}
                        </option>
                    ))}
                </select>
            </div>
            
            <div className="form-group">
                <label htmlFor="employee_id" className="form-label">Manager *</label>
                <select
                    id="employee_id"
                    name="employee_id"
                    className="form-select"
                    value={formData.employee_id}
                    onChange={handleInputChange}
                    disabled={loading}
                >
                    <option value="">Select Manager</option>
                    {sortedByName(employees).map(employee => (
                        <option key={employee.employee_id} value={employee.employee_id}>
                        {employee.name}
                        </option>
                    ))}
                </select>
            </div>
            
            <div className="form-group">
                <label htmlFor="khata_no" className="form-label">Khata Number *</label>
                <input
                    type="number"
                    id="khata_no"
                    name="khata_no"
                    className="form-input"
                    value={formData.khata_no}
                    onChange={handleInputChange}
                    disabled={loading}
                    placeholder="Enter khata number"
                />
            </div>

            <div className="form-row">
                <div className="form-group">
                    <label htmlFor="total_kila" className="form-label">Total Kila *</label>
                    <input
                        type="number"
                        id="total_kila"
                        name="total_kila"
                        className="form-input"
                        value={formData.total_kila}
                        onChange={handleInputChange}
                        disabled={loading}
                        placeholder="Total capacity"
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="kila_occupied" className="form-label">Kila Occupied *</label>
                    <input
                        type="number"
                        id="kila_occupied"
                        name="kila_occupied"
                        className="form-input"
                        value={formData.kila_occupied}
                        onChange={handleInputChange}
                        disabled={loading}
                        placeholder="Currently occupied"
                    />
                </div>
            </div>
            
            <div className="form-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '2rem' }}>
                <button type="button" className="btn btn-secondary" onClick={handleCancel} disabled={loading}>
                    Cancel
                </button>
                <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={loading}>
                    <Check size={18} />
                    {loading ? 'Saving...' : (editingItem ? 'Update' : 'Save Khata')}
                </button>
            </div>
        </div>
      </>
    );
  }

  // List View
  return renderLayout(
    <>
      <div className="page-header">
        <h1 className="page-title">
          Khata Management
        </h1>
        <button className="btn btn-primary" onClick={handleAdd}>
          <Plus size={18} /> Add Khata
        </button>
      </div>
      
      <div className="filter-bar">
        <div className="filter-group">
          <label className="form-label" style={{marginBottom: 0, fontSize: '0.8rem'}}>Tabela</label>
          <input
            type="text"
            name="tabela_name"
            className="form-input"
            value={filters.tabela_name}
            onChange={handleFilterChange}
            placeholder="Search Tabela"
          />
        </div>
        <div className="filter-group">
          <label className="form-label" style={{marginBottom: 0, fontSize: '0.8rem'}}>Manager</label>
          <input
            type="text"
            name="employee_name"
            className="form-input"
            value={filters.employee_name}
            onChange={handleFilterChange}
            placeholder="Search Manager"
          />
        </div>
        <div className="filter-group">
          <label className="form-label" style={{marginBottom: 0, fontSize: '0.8rem'}}>Khata No</label>
          <input
            type="text"
            name="khata_no"
            className="form-input"
            value={filters.khata_no}
            onChange={handleFilterChange}
            placeholder="Search #"
          />
        </div>
        <button className="btn btn-secondary" onClick={clearFilters} style={{ height: '42px' }}>
          Clear
        </button>
      </div>
      
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Tabela</th>
              <th>Manager</th>
              <th>Khata No</th>
              <th>Total Kila</th>
              <th>Occupied</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentItems.map(item => (
              <tr key={`${item.tabela_id}-${item.khata_no}`}>
                <td data-label="ID">{item.khata_id}</td>
                <td data-label="Tabela" style={{ fontWeight: '500' }}>{item.tabela_name || getTabelaName(item.tabela_id)}</td>
                <td data-label="Manager">{item.employee_name || getEmployeeName(item.employee_id)}</td>
                <td data-label="Khata No"><span style={{ background: '#e0f2fe', color: '#0369a1', padding: '2px 8px', borderRadius: '4px', fontWeight: '500' }}>#{item.khata_no}</span></td>
                <td data-label="Total Kila">{item.total_kila}</td>
                <td data-label="Occupied">{item.kila_occupied}</td>
                <td data-label="Actions">
                  <div className="actions-cell">
                    <button 
                      className="btn-icon"
                      onClick={() => handleView(item)}
                      title="View Details"
                    >
                      <Eye size={18} />
                    </button>
                    <button 
                      className="btn-icon"
                      onClick={() => handleEdit(item)}
                      title="Edit"
                    >
                      <Edit size={18} />
                    </button>
                    <button 
                      className="btn-icon"
                      style={{ color: 'var(--error-color)' }}
                      onClick={() => handleDelete(item)}
                      title="Delete"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {currentItems.length === 0 && !loading && (
          <div className="no-data">
            <p>No khatas found matching your filters</p>
          </div>
        )}
      </div>
      
      {totalPages > 1 && (
        <div className="pagination">
          <button 
            className="pagination-btn"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            <ChevronLeft size={16} />
          </button>
          
          {[...Array(totalPages)].map((_, index) => (
            <button
              key={index + 1}
              className={`pagination-btn ${currentPage === index + 1 ? 'active' : ''}`}
              onClick={() => handlePageChange(index + 1)}
            >
              {index + 1}
            </button>
          ))}
          
          <button 
            className="pagination-btn"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}
      
       <div style={{ textAlign: 'center', marginTop: '1rem', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
        Showing {currentItems.length} of {filteredData.length} records
      </div>
    </>
  );
}

export default KhataPage;